/* ==========================================================================
   Royal Honey — product
   Media gallery & zoom · variant configurator (section rendering) ·
   recommendations · sticky buy bar · complete-the-look bundle
   ========================================================================== */
(() => {
  'use strict';

  const N = window.SR || { routes: {}, strings: {} };
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const define = (name, cls) => { if (!customElements.get(name)) customElements.define(name, cls); };
  const parse = (html) => new DOMParser().parseFromString(html, 'text/html');

  /* ---------- Media gallery ---------- */
  define('media-gallery', class extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      if (!this.track) return;
      this.items = [...this.track.querySelectorAll('[data-media-id]')];
      this.thumbs = [...this.querySelectorAll('[data-thumb]')];
      this.counter = this.querySelector('[data-gallery-index]');
      this.thumbs.forEach((t) => t.addEventListener('click', () => this.select(t.dataset.thumb)));
      this.querySelector('[data-gallery-prev]')?.addEventListener('click', () => this.step(-1));
      this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.step(1));
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => { if (entry.isIntersecting) this.mark(entry.target.dataset.mediaId); });
      }, { root: this.track, threshold: 0.6 });
      this.items.forEach((item) => this.observer.observe(item));
      if (this.dataset.featured) requestAnimationFrame(() => this.select(this.dataset.featured, false));

      this.lightbox = this.querySelector('[data-lightbox]');
      if (this.lightbox) {
        this.querySelectorAll('[data-zoom]').forEach((btn) => btn.addEventListener('click', () => this.zoom(btn.dataset.zoom)));
        this.lightbox.querySelector('[data-lightbox-close]')?.addEventListener('click', () => this.lightbox.close());
        this.lightbox.querySelectorAll('[data-lightbox-item]').forEach((fig) => fig.addEventListener('click', () => fig.classList.toggle('is-zoomed')));
      }
    }
    disconnectedCallback() { this.observer?.disconnect(); }
    current() { return this.items.findIndex((i) => i.classList.contains('is-active')); }
    step(dir) {
      const i = this.current();
      const next = this.items[(Math.max(i, 0) + dir + this.items.length) % this.items.length];
      if (next) this.select(next.dataset.mediaId);
    }
    select(id, smooth = true) {
      const item = this.items.find((i) => i.dataset.mediaId === String(id));
      if (!item) return;
      const stacked = getComputedStyle(this.track).flexDirection === 'column' || this.track.scrollWidth <= this.track.clientWidth + 2;
      if (stacked) {
        if (smooth) item.scrollIntoView({ behavior: reduceMotion.matches ? 'auto' : 'smooth', block: 'nearest' });
      } else {
        this.track.scrollTo({ left: item.offsetLeft - this.track.offsetLeft, behavior: smooth && !reduceMotion.matches ? 'smooth' : 'auto' });
      }
      this.mark(id);
    }
    mark(id) {
      this.items.forEach((item, n) => {
        const on = item.dataset.mediaId === String(id);
        item.classList.toggle('is-active', on);
        if (on && this.counter) this.counter.textContent = String(n + 1);
        if (!on) item.querySelectorAll('video').forEach((v) => v.pause());
      });
      this.thumbs.forEach((t) => {
        const on = t.dataset.thumb === String(id);
        t.classList.toggle('is-active', on);
        if (on) t.setAttribute('aria-current', 'true'); else t.removeAttribute('aria-current');
      });
    }
    zoom(id) {
      if (!this.lightbox) return;
      this.lightbox.showModal();
      const fig = this.lightbox.querySelector(`[data-lightbox-item="${id}"]`);
      requestAnimationFrame(() => fig?.scrollIntoView({ inline: 'center', block: 'nearest' }));
    }
  });

  /* ---------- Variant configurator ---------- */
  define('product-info', class extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.section;
      this.form = document.getElementById(this.dataset.form);
      this.scope = this.closest('[data-product-scope]') || this.closest('.section') || this;
      this.addEventListener('change', (e) => { if (e.target.matches('variant-picker input[type="radio"]')) this.onChange(); });
      this.initBuybar();
    }
    get variants() {
      if (!this._variants) {
        const json = this.querySelector('[data-variants]');
        try { this._variants = json ? JSON.parse(json.textContent) : []; } catch (err) { this._variants = []; }
      }
      return this._variants;
    }
    selected() {
      return [...this.querySelectorAll('variant-picker fieldset')].map((g) => g.querySelector('input:checked')?.value);
    }
    onChange() {
      const options = this.selected();
      this.querySelectorAll('variant-picker fieldset').forEach((g) => {
        const label = g.querySelector('[data-selected-label]');
        const checked = g.querySelector('input:checked');
        if (label && checked) label.textContent = checked.value;
      });
      const variant = this.variants.find((v) => v.options.every((o, i) => o === options[i]));
      if (!variant) { this.setUnavailable(); return; }
      const input = this.form?.querySelector('[data-variant-id]');
      if (input) { input.value = variant.id; input.dispatchEvent(new Event('change', { bubbles: true })); }
      if (this.dataset.updateUrl === 'true') window.history.replaceState({}, '', `${this.dataset.url}?variant=${variant.id}`);
      if (variant.featured_media) this.querySelector('media-gallery')?.select(variant.featured_media.id);
      this.updateWhatsApp(variant.id);
      this.render(variant.id);
    }
    updateWhatsApp(variantId) {
      this.querySelectorAll('[data-wa-order]').forEach((link) => {
        let map = {};
        try { map = JSON.parse(link.parentElement.querySelector('[data-wa-variants]')?.textContent || '{}'); } catch (err) { return; }
        const v = map[variantId];
        if (!v) return;
        const message = (link.dataset.template || '')
          .replace('{produit}', link.dataset.product || '').replace('{product}', link.dataset.product || '')
          .replace('{variante}', v.t).replace('{variant}', v.t)
          .replace('{prix}', v.p).replace('{price}', v.p)
          .replace('{lien}', `${link.dataset.origin || ''}${v.u}`).replace('{link}', `${link.dataset.origin || ''}${v.u}`)
          .replace(' ()', '');
        link.href = `${(link.dataset.numberLink || '').trim()}?text=${encodeURIComponent(message)}`;
      });
    }
    setUnavailable() {
      const btn = this.querySelector('[data-add]');
      if (btn) {
        btn.disabled = true;
        const label = btn.querySelector('[data-add-label]');
        if (label) label.textContent = N.strings.unavailable;
      }
      const bar = this.scope.querySelector('[data-buybar-add]');
      if (bar) bar.disabled = true;
      const dynamic = this.querySelector('.buy__dynamic');
      if (dynamic) dynamic.hidden = true;
      this.scope.querySelectorAll('delivery-estimate').forEach((el) => { el.setAttribute('data-unavailable', ''); el.hidden = true; });
    }
    async render(variantId) {
      this.controller?.abort();
      this.controller = new AbortController();
      try {
        const res = await fetch(`${this.dataset.url}?variant=${variantId}&section_id=${this.sectionId}`, { signal: this.controller.signal });
        const doc = parse(await res.text());
        const active = document.activeElement;
        const focusKey = active && active.matches('variant-picker input') ? `${active.name}::${active.value}` : null;
        ['price', 'buy', 'stock', 'picker', 'packs', 'buybar-price', 'buybar-variant'].forEach((key) => {
          const src = doc.querySelector(`[data-swap="${key}"]`);
          this.scope.querySelectorAll(`[data-swap="${key}"]`).forEach((dst) => { if (src) dst.innerHTML = src.innerHTML; });
        });
        if (focusKey) {
          const [name, value] = focusKey.split('::');
          [...this.querySelectorAll('variant-picker input')].find((i) => i.name === name && i.value === value)?.focus();
        }
        const add = this.querySelector('[data-add]');
        const bar = this.scope.querySelector('[data-buybar-add]');
        if (bar && add) bar.disabled = add.disabled;
        const dynamic = this.querySelector('.buy__dynamic');
        if (dynamic && add) dynamic.hidden = add.disabled;
        // No delivery promise for a sold-out variant.
        if (add) this.scope.querySelectorAll('delivery-estimate').forEach((el) => { el.toggleAttribute('data-unavailable', add.disabled); el.hidden = add.disabled; });
        window.SR?.utils?.bus.emit('variant:rendered', { scope: this.scope, variantId });
      } catch (err) {
        if (err.name !== 'AbortError') console.error(err);
      }
    }
    initBuybar() {
      const bar = this.scope.querySelector('[data-buybar]');
      const target = this.querySelector('.buy__actions');
      if (!bar || !target) return;
      bar.hidden = false;
      let pastBuy = false;
      let nearFooter = false;
      const sync = () => bar.classList.toggle('is-visible', pastBuy && !nearFooter);
      new IntersectionObserver(([entry]) => { pastBuy = !entry.isIntersecting && entry.boundingClientRect.top < 0; sync(); }).observe(target);
      const footer = document.querySelector('.footer');
      if (footer) new IntersectionObserver(([entry]) => { nearFooter = entry.isIntersecting; sync(); }).observe(footer);
    }
  });

  /* ---------- Recommendations ---------- */
  define('product-recommendations', class extends HTMLElement {
    connectedCallback() {
      if (this.children.length || !this.dataset.url) return;
      const load = async () => {
        try {
          const res = await fetch(this.dataset.url);
          const fresh = parse(await res.text()).querySelector('product-recommendations');
          if (fresh && fresh.innerHTML.trim()) {
            this.innerHTML = fresh.innerHTML;
            this.closest('.section')?.classList.remove('is-pending');
            window.SR?.utils?.bus.emit('cards:loaded', this);
          } else this.closest('.section')?.setAttribute('hidden', '');
        } catch (err) { console.error(err); }
      };
      new IntersectionObserver((entries, obs) => {
        if (!entries[0].isIntersecting) return;
        obs.disconnect();
        load();
      }, { rootMargin: '0px 0px 400px 0px' }).observe(this);
    }
  });

  /* ---------- Complete the look: add several products at once ---------- */
  define('bundle-picker', class extends HTMLElement {
    connectedCallback() {
      this.items = [...this.querySelectorAll('[data-bundle-item]')];
      this.total = this.querySelector('[data-bundle-total]');
      this.count = this.querySelector('[data-bundle-count]');
      this.button = this.querySelector('[data-bundle-add]');
      this.format = this.dataset.moneyFormat || '{{amount_with_comma_separator}} €';
      this.addEventListener('change', () => this.update());
      this.button?.addEventListener('click', () => this.add());
      this.update();
    }
    money(cents) {
      const value = (cents / 100).toFixed(2);
      const [int, dec] = value.split('.');
      const spaced = int.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
      return this.format
        .replace(/{{\s*amount_with_comma_separator\s*}}/, `${spaced},${dec}`)
        .replace(/{{\s*amount_no_decimals_with_comma_separator\s*}}/, spaced)
        .replace(/{{\s*amount_no_decimals\s*}}/, int)
        .replace(/{{\s*amount\s*}}/, `${int}.${dec}`);
    }
    chosen() {
      return this.items.filter((item) => item.querySelector('[data-bundle-check]')?.checked).map((item) => {
        const select = item.querySelector('[data-bundle-variant]');
        const option = select ? select.selectedOptions[0] : null;
        return { id: parseInt(option ? option.value : item.dataset.variant, 10), price: parseInt(option ? option.dataset.price : item.dataset.price, 10) || 0, available: option ? !option.disabled : item.dataset.available === 'true' };
      });
    }
    update() {
      const picked = this.chosen();
      const sum = picked.reduce((acc, p) => acc + p.price, 0);
      if (this.total) this.total.textContent = this.money(sum);
      if (this.count) this.count.textContent = String(picked.length);
      if (this.button) this.button.disabled = !picked.length || picked.some((p) => !p.available);
      this.items.forEach((item) => item.classList.toggle('is-off', !item.querySelector('[data-bundle-check]')?.checked));
    }
    async add() {
      const picked = this.chosen();
      if (!picked.length || !N.cart) return;
      this.button.classList.add('is-loading');
      try {
        await N.cart.cartPost(`${N.routes.cartAdd}.js`, { items: picked.map((p) => ({ id: p.id, quantity: 1 })) });
        const drawer = document.querySelector('cart-drawer');
        if (drawer && N.settings.cartType === 'drawer') drawer.open(this.button); else window.location.href = N.routes.cart;
      } catch (err) {
        window.SR?.utils?.toast(err.message || N.strings.error);
      } finally {
        this.button.classList.remove('is-loading');
      }
    }
  });
})();
